import { useEffect, useRef, useState } from 'react';

/** How far (px, after resistance) the page must be pulled before letting go refreshes it. */
export const PULL_THRESHOLD = 64;
const MAX_PULL = 96;
const RESISTANCE = 0.5;

/**
 * Pull down from the very top of the page to refresh, as in a native app. An installed iOS web app has no pull-to-refresh
 * of its own, and Chrome's reloads the whole page, so the page turns the browser's off while this is enabled and draws
 * its own indicator from `pull` and `refreshing`.
 */
export function usePullToRefresh(onRefresh: () => Promise<unknown>, enabled = true): { pull: number; refreshing: boolean } {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const start = useRef<number | null>(null);
  const pullRef = useRef(0);
  const busy = useRef(false);
  const refresh = useRef(onRefresh);
  refresh.current = onRefresh;

  useEffect(() => {
    if (!enabled) return;
    const root = document.documentElement;
    const before = root.style.overscrollBehaviorY;
    root.style.overscrollBehaviorY = 'contain';
    const set = (v: number) => { pullRef.current = v; setPull(v); };
    const onStart = (e: TouchEvent) => {
      start.current = !busy.current && window.scrollY <= 0 && e.touches.length === 1 ? e.touches[0].clientY : null;
    };
    const onMove = (e: TouchEvent) => {
      if (start.current === null) return;
      const dy = e.touches[0].clientY - start.current;
      if (dy <= 0 || window.scrollY > 0) { set(0); return; }
      set(Math.min(MAX_PULL, dy * RESISTANCE));
    };
    const onEnd = () => {
      if (start.current === null) return;
      start.current = null;
      if (pullRef.current < PULL_THRESHOLD) { set(0); return; }
      busy.current = true;
      setRefreshing(true);
      set(PULL_THRESHOLD);
      void Promise.resolve(refresh.current()).catch(() => undefined).finally(() => {
        busy.current = false;
        setRefreshing(false);
        set(0);
      });
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      root.style.overscrollBehaviorY = before;
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled]);

  return { pull, refreshing };
}
